<template>
  <NodeViewWrapper
    class="mam-figure"
    :class="{ 'is-selected': selected }"
    @mousedown.capture="onMouseDownCapture"
    @click.capture="onClickCapture"
    @dblclick.capture="onDblClickCapture"
  >
    <div class="mam-figure__media" contenteditable="false">
      <a
        v-if="node.attrs.link"
        class="mam-figure__link"
        :href="node.attrs.link.href"
        :target="node.attrs.link.target"
        :rel="node.attrs.link.rel"
        @click.prevent
        @mousedown.prevent
      >
        <img
          class="mam-figure__img"
          :src="node.attrs.src"
          :alt="node.attrs.alt"
          :title="node.attrs.title"
          :width="node.attrs.width"
          :height="node.attrs.height"
          :id="node.attrs.id"
          :longdesc="node.attrs.longdesc"
          :data-node-id="node.attrs['data-node-id']"
          draggable="false"
        />
      </a>

      <img
        v-else
        class="mam-figure__img"
        :src="node.attrs.src"
        :alt="node.attrs.alt"
        :title="node.attrs.title"
        :width="node.attrs.width"
        :height="node.attrs.height"
        :id="node.attrs.id"
        :longdesc="node.attrs.longdesc"
        :data-node-id="node.attrs['data-node-id']"
        draggable="false"
      />
    </div>

    <figcaption class="mam-figure__caption">
      <NodeViewContent />
    </figcaption>
  </NodeViewWrapper>
</template>

<script setup lang="ts">
import { NodeSelection } from '@tiptap/pm/state'
import { NodeViewWrapper, NodeViewContent, nodeViewProps } from '@tiptap/vue-3'

const props = defineProps(nodeViewProps)

function shouldSelectNode(target: EventTarget | null) {
  const el = target as HTMLElement | null
  if (!el) return false

  if (el.closest('.mam-figure__caption')) return false

  if (el.closest('.mam-figure__media') || el.closest('.mam-figure')) return true

  return false
}

function selectThisNode() {
  const { editor, getPos } = props

  if (typeof getPos !== 'function') return

  const pos = getPos()
  if (typeof pos !== 'number') return

  editor.view.dispatch(
    editor.state.tr.setSelection(
      NodeSelection.create(editor.state.doc, pos)
    )
  )
}

function onMouseDownCapture(e: MouseEvent) {
  if (shouldSelectNode(e.target)) {
    e.preventDefault()
    selectThisNode()
  }
}

function onClickCapture(e: MouseEvent) {
  if (shouldSelectNode(e.target)) {
    e.preventDefault()
    selectThisNode()
  }
}

// TODO: fix: augment Tiptap’s EditorEvents
// add a .d.ts file (e.g. src/types/tiptap-events.d.ts)
function onDblClickCapture(e: MouseEvent) {
  if (shouldSelectNode(e.target)) {
    e.preventDefault()
    selectThisNode()
    ;(props.editor as any).emit('mam:open-image-modal')
  }
}
</script>

<style scoped>
.mam-figure {
  display: inline-block;
  margin: 0;
}

.mam-figure__media {
  display: inline-block;
}

.mam-figure__img {
  display: block;
  max-width: 100%;
}

.mam-figure__caption {
  margin-left: 4px;
  overflow-wrap: break-word;
  user-select: text;
}

/* TipTap/ProseMirror selection class when node is selected */
.mam-figure.is-selected,
.mam-figure.ProseMirror-selectednode {
  outline: 2px solid #1c385f;
  border-radius: 8px;
  margin: 0px;
}
</style>
